import { DateTime } from "luxon";
import { Coverage } from "@spotpetinsurance/spot-types/entities/Coverage";
import { PublicConfig } from "../PublicConfig";
import Strings from "./Strings.constants";
import { CoverageSettings, Quote } from "../types/Quote.interface";
import { TMP_POLICY_PREFIX } from "./constants";
import { PlanFilterUtils } from "./PlanFilterUtils";

type QuickEnrollCoverageSettingsParams = {
    /** The existing (already enrolled) pet's coverage, as the add-a-pet API reports it. */
    existing: CoverageSettings;
    /** The new pet's current coverages, whose API-returned `name` / `createDate` / `startDate` must survive the copy. */
    currentCoverages: CoverageSettings["coverages"] | undefined;
    /** The quote's rating state (`ratingAddress.state`); the copied plan is remapped for it. */
    ratingState: string | undefined;
};

export class AddPetDataUtils {
    /**
     * ENG-20661: the coverage quick enroll installs on the NEW pet from an existing pet's plan. This is the
     * only place that copy is built, for the carousel sync (QuickEnrollPlans) and for Select Plan (the
     * add-a-pet coverage step), so both persist the same plan.
     *
     * The existing plan is remapped for the quote's rating state FIRST (PlanFilterUtils, ENG-17824): a
     * California policy sold before CA dropped the $2,500 / $3,000 / $4,000 limits and the $1,000
     * deductible still carries them, and copying them verbatim had the underwriter reject every
     * add-a-pet PUT ("Annual limit $2,500.00 option is not found on matching policy"). The remap tables
     * only have a CA entry, so other states copy the plan unchanged. The grandfathered policy itself is
     * never touched.
     *
     * The merge then keeps the new pet's API-returned dates and name per coverage type, exactly as before.
     */
    static quickEnrollCoverageSettings({ existing, currentCoverages, ratingState }: QuickEnrollCoverageSettingsParams): CoverageSettings {
        const { amounts, extra, coverages } = PlanFilterUtils.remapCoverageSettingsForState(existing, ratingState);
        return {
            amounts,
            extra,
            coverages: coverages?.map(({ type, id, preventivePrice, extra: coverageExtra }) => {
                const currentMatch = currentCoverages?.find(coverage => coverage.type === type);
                return {
                    type,
                    id,
                    ...(currentMatch?.name !== undefined && { name: currentMatch.name }),
                    ...(currentMatch?.createDate !== undefined && { createDate: currentMatch.createDate }),
                    ...(currentMatch?.startDate !== undefined && { startDate: currentMatch.startDate }),
                    ...(preventivePrice !== undefined && { preventivePrice }),
                    ...(coverageExtra !== undefined && { extra: coverageExtra })
                };
            })
        };
    }

    static mapPoliciesToAddPetPets(policies: Quote["policies"], defaultCoverageSettings: CoverageSettings) {
        return (
            policies?.map(policy => {
                let birthDate = "";
                if (policy.birthMonth && policy.birthYear) {
                    birthDate = DateTime.utc(parseInt(policy.birthYear), parseInt(policy.birthMonth), 1).toISODate() ?? "";
                } else if (typeof policy.age === "number") {
                    const age = policy.age === 0 ? 10 / 12 : policy.age;
                    birthDate = DateTime.utc().minus({ year: age }).toISODate() ?? "";
                }

                if (!birthDate) {
                    throw new Error(`Missing birth date for pet "${policy.name ?? "unknown"}": no birthMonth/birthYear or age available`);
                }

                const settings = policy.coverageSettings ?? defaultCoverageSettings;
                const coverages: Coverage[] = [];

                if (settings?.coverages) {
                    const accidentCoverage = settings.coverages.find(c => c.type === "accident");
                    const illnessCoverage = settings.coverages.find(c => c.type === "illness");

                    if (accidentCoverage || illnessCoverage) {
                        const baseCoverage: Coverage = {
                            type: [],
                            id: accidentCoverage?.id,
                            details: {}
                        };

                        if (accidentCoverage) baseCoverage.type!.push("accident");
                        if (illnessCoverage) baseCoverage.type!.push("illness");

                        if (settings.amounts?.reimbursementRate !== undefined) {
                            baseCoverage.details!.reimbursementPercent = { value: settings.amounts.reimbursementRate, symbol: "%" };
                        }
                        if (settings.amounts?.annualDeductible !== undefined) {
                            baseCoverage.details!.deductible = { value: settings.amounts.annualDeductible, symbol: "$" };
                        }
                        if (settings.amounts?.annualLimit !== undefined) {
                            baseCoverage.details!.annualLimit = {
                                value: settings.amounts.annualLimit,
                                symbol: settings.amounts.annualLimit === PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE ? Strings.UNLIMITED_ANNUAL_LIMIT_LABEL : "$"
                            };
                        }

                        coverages.push(baseCoverage);
                    }

                    const wellnessCoverage = settings.coverages.find(c => c.type === "wellness-gold" || c.type === "wellness-platinum");
                    if (wellnessCoverage) {
                        coverages.push({ type: ["wellness"], id: wellnessCoverage.id });
                    }
                }

                return {
                    petID: policy.id && policy.id.startsWith(TMP_POLICY_PREFIX) ? undefined : policy.id,
                    name: policy.name ?? "",
                    species: policy.species ?? "",
                    breedID: policy.breedID ?? "",
                    birthDate,
                    gender: (policy.gender ?? "M") as "F" | "M",
                    coverages
                };
            }) ?? []
        );
    }
}
